/*
  Favorieten: hartjes op producten, recepten en kenniscentrum-artikelen,
  een teller in de header en de pagina /pages/favorieten
  (sections/fts-favorieten.liquid).

  Eén lijst met sleutels: een producthandle ("magnesium-citraat"), een
  recept als "recept:<handle>" of een artikel als "artikel:<blog>/<handle>".
  Hartjes op productkaarten maakt dit script zelf; hartjes bij recepten en
  artikelen staan al in de HTML (snippets/fts-hart.liquid) en worden hier
  alleen gebonden.

  Opslag
  - Altijd in de browser (localStorage), zodat het zonder account werkt.
  - Ben je ingelogd, dan hoort de lijst bij je klantaccount: bij het laden
    wordt de accountlijst (metafield customer.fts.favorieten, via Liquid
    op de pagina gezet) samengevoegd met die van dit apparaat, en elke
    wijziging gaat naar de eigen Food to Shine-app via de app-proxy
    /apps/fts/favorieten. Bestaat die route (nog) niet, dan blijft het
    stil bij de browseropslag; er gaat niets mis.

  Bewust niet: meldingen bij uitverkocht, 'binnenkort', tags.
*/
import { CartLinesUpdateEvent } from '@shopify/events';

const SLEUTEL = 'fts-favorieten';
const SYNC = '/apps/fts/favorieten';
const klant = document.documentElement.dataset.ftsKlant || '';
const euro = new Intl.NumberFormat('nl-NL', { style: 'currency', currency: 'EUR' });

/* --- Opslag ---------------------------------------------------------- */

const favorieten = {
  lees() {
    try { return JSON.parse(localStorage.getItem(SLEUTEL)) || []; } catch { return []; }
  },
  schrijf(lijst, { stil = false } = {}) {
    try { localStorage.setItem(SLEUTEL, JSON.stringify(lijst)); } catch { /* privémodus */ }
    document.dispatchEvent(new CustomEvent('fts:favorieten', { detail: lijst }));
    if (!stil) account.bewaar(lijst);
  },
  heeft(handle) { return this.lees().includes(handle); },
  producten() { return this.lees().filter((h) => !h.includes(':')); },
  recepten() { return this.lees().filter((h) => h.startsWith('recept:')); },
  artikelen() { return this.lees().filter((h) => h.startsWith('artikel:')); },
  wissel(handle) {
    const lijst = this.lees();
    const i = lijst.indexOf(handle);
    if (i === -1) lijst.unshift(handle); else lijst.splice(i, 1);
    this.schrijf(lijst);
    return i === -1;
  },
};

/* --- Koppeling met het klantaccount ------------------------------------ */

const account = {
  werkt: Boolean(klant),
  timer: null,
  start() {
    if (!klant) return;
    let vanAccount = [];
    try { vanAccount = JSON.parse(document.getElementById('fts-favorieten-account')?.textContent || '[]'); } catch { /* leeg */ }
    if (typeof vanAccount === 'string') { try { vanAccount = JSON.parse(vanAccount); } catch { vanAccount = []; } }
    if (!Array.isArray(vanAccount)) vanAccount = [];
    const lokaal = favorieten.lees();
    const samen = [...new Set([...lokaal, ...vanAccount])];
    if (samen.length !== lokaal.length) favorieten.schrijf(samen, { stil: true });
    if (samen.length !== vanAccount.length) this.bewaar(samen);
  },
  bewaar(lijst) {
    if (!this.werkt) return;
    clearTimeout(this.timer);
    this.timer = setTimeout(async () => {
      try {
        const r = await fetch(SYNC, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ favorieten: lijst }),
        });
        if (!r.ok) this.werkt = false;   // app nog niet actief: alleen browseropslag
      } catch { this.werkt = false; }
    }, 600);
  },
};

/* --- Productgegevens ----------------------------------------------------- */

const handleUitUrl = (url) => {
  const m = String(url || '').match(/\/products\/([^/?#]+)/);
  return m ? decodeURIComponent(m[1]) : null;
};

const cache = new Map();
function haalProduct(handle) {
  if (!cache.has(handle)) {
    cache.set(handle, fetch(`/products/${encodeURIComponent(handle)}.js`)
      .then((r) => (r.ok ? r.json() : null)).catch(() => null));
  }
  return cache.get(handle);
}

async function inMandje(regels) {
  const uitgesteld = CartLinesUpdateEvent.createPromise();
  document.dispatchEvent(new CartLinesUpdateEvent({
    action: 'add',
    context: 'product',
    lines: regels.map((r) => ({ merchandiseId: r.id, quantity: r.quantity })),
    promise: uitgesteld.promise,
  }));
  const antwoord = await fetch('/cart/add.js', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify({ items: regels.map((r) => ({ id: Number(r.id), quantity: r.quantity })) }),
  }).then((r) => r.json()).catch(() => ({ status: 500 }));
  const cart = await fetch('/cart.js').then((r) => r.json()).catch(() => null);
  if (cart) {
    uitgesteld.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
      detail: { didError: Boolean(antwoord.status), items: cart.items, source: 'fts-favorieten', itemCount: cart.item_count },
    });
  }
  return !antwoord.status;
}

/* --- Hartjes ------------------------------------------------------------ */

const HART = '<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M12 20s-7.5-4.6-7.5-10A4.5 4.5 0 0 1 12 7a4.5 4.5 0 0 1 7.5 3c0 5.4-7.5 10-7.5 10z"/></svg>';

function zetHart(knop) {
  const aan = favorieten.heeft(knop.dataset.handle);
  const titel = knop.dataset.titel || 'Dit item';
  knop.setAttribute('aria-pressed', String(aan));
  knop.setAttribute('aria-label', aan ? `${titel} uit je favorieten halen` : `${titel} bewaren bij je favorieten`);
  const tekst = knop.querySelector('span');
  if (tekst) {
    tekst.textContent = aan
      ? (knop.dataset.tekstAan || 'Staat in mijn favorieten')
      : (knop.dataset.tekstUit || 'Bewaar in mijn favorieten');
  }
}

// Hartjes die al in de HTML staan (recepten): één keer binden
function bindHartjes(root = document) {
  root.querySelectorAll('.fts-hart[data-handle]:not([data-fts-gebonden])').forEach((knop) => {
    knop.dataset.ftsGebonden = '';
    zetHart(knop);
    knop.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
      favorieten.wissel(knop.dataset.handle);
    });
  });
}

function hartKnop(handle, titel, variant) {
  const knop = document.createElement('button');
  knop.type = 'button';
  knop.className = `fts-hart fts-hart--${variant}`;
  knop.dataset.handle = handle;
  knop.dataset.titel = titel;
  knop.dataset.ftsGebonden = '';
  knop.innerHTML = HART + (variant === 'groot' ? '<span></span>' : '');
  zetHart(knop);
  knop.addEventListener('click', (e) => {
    e.preventDefault();
    e.stopPropagation();
    favorieten.wissel(handle);
  });
  return knop;
}

function hartjesOpKaarten(root = document) {
  root.querySelectorAll('product-card:not([data-fts-hart])').forEach((kaart) => {
    const link = kaart.querySelector('a[href*="/products/"]');
    const handle = handleUitUrl(link?.getAttribute('href'));
    if (!handle) return;
    kaart.dataset.ftsHart = '';
    kaart.append(hartKnop(handle, link.getAttribute('aria-label') || link.textContent.trim(), 'klein'));
  });
}

function hartOpProductpagina() {
  const handle = location.pathname.includes('/products/') ? handleUitUrl(location.pathname) : null;
  if (!handle) return;
  const knoppen = document.querySelector('.buy-buttons-block');
  if (!knoppen || knoppen.parentElement.querySelector('.fts-hart--groot')) return;
  knoppen.after(hartKnop(handle, document.querySelector('h1')?.textContent.trim() || '', 'groot'));
}

/* --- Teller in de header ------------------------------------------------ */

function tellers() {
  const n = favorieten.lees().length;
  document.querySelectorAll('[data-fts-favorieten-teller]').forEach((t) => {
    t.textContent = n ? String(n) : '';
    t.hidden = !n;
  });
}

/* --- Favorietenpagina --------------------------------------------------- */

function favorietenPagina(root) {
  const lijstEl = root.querySelector('[data-fts-lijst]');
  const receptenEl = root.querySelector('[data-fts-recepten-lijst]');
  const artikelenEl = root.querySelector('[data-fts-artikelen-lijst]');
  const kopProducten = root.querySelector('[data-fts-kop-producten]');
  const kopRecepten = root.querySelector('[data-fts-kop-recepten]');
  const kopArtikelen = root.querySelector('[data-fts-kop-artikelen]');
  const leeg = root.querySelector('[data-fts-leeg]');
  const alles = root.querySelector('[data-fts-alles]');

  // Recepten en artikelen staan allemaal verborgen in de HTML; toon de
  // bewaarde, in de volgorde van de lijst (nieuwste bovenaan)
  function tekenVoorgerenderd(el, kop, ids) {
    if (!el) return 0;
    el.querySelectorAll('[data-favoriet]').forEach((li) => { li.hidden = true; });
    let n = 0;
    ids.forEach((id) => {
      const li = el.querySelector(`[data-favoriet="${CSS.escape(id)}"]`);
      if (!li) return;   // bestaat niet meer
      li.hidden = false;
      el.append(li);
      n++;
    });
    if (kop) kop.hidden = n === 0;
    return n;
  }

  async function teken() {
    const nRecepten = tekenVoorgerenderd(receptenEl, kopRecepten, favorieten.recepten());
    const nArtikelen = tekenVoorgerenderd(artikelenEl, kopArtikelen, favorieten.artikelen());
    const handles = favorieten.producten();
    leeg.hidden = handles.length + nRecepten + nArtikelen > 0;
    alles.hidden = handles.length === 0;
    const producten = (await Promise.all(handles.map(haalProduct))).filter(Boolean);
    if (kopProducten) kopProducten.hidden = producten.length === 0;
    lijstEl.replaceChildren(...producten.map((p) => {
      const variant = p.variants.find((v) => v.available) || null;
      const li = document.createElement('li');
      li.className = 'fts-favoriet';
      const beeld = p.featured_image ? `${p.featured_image.split('?')[0]}?width=300` : '';
      li.innerHTML = `
        <a class="fts-favoriet__beeld" href="${p.url}" tabindex="-1" aria-hidden="true">${beeld ? `<img src="${beeld}" alt="" loading="lazy" width="150" height="150">` : ''}</a>
        <div class="fts-favoriet__tekst">
          <a class="fts-favoriet__titel" href="${p.url}"></a>
          <p class="fts-favoriet__prijs">${euro.format((variant || p.variants[0]).price / 100)}</p>
        </div>
        <div class="fts-favoriet__acties">
          <button type="button" class="button fts-favoriet__mandje" ${variant ? '' : 'disabled'}><span>${variant ? 'In winkelwagen' : 'Niet leverbaar'}</span></button>
        </div>`;
      li.querySelector('.fts-favoriet__titel').textContent = p.title;
      li.querySelector('.fts-favoriet__acties').append(hartKnop(p.handle, p.title, 'klein'));
      const knop = li.querySelector('.fts-favoriet__mandje');
      if (variant) {
        knop.addEventListener('click', async () => {
          knop.disabled = true;
          const gelukt = await inMandje([{ id: variant.id, quantity: 1 }]);
          knop.querySelector('span').textContent = gelukt ? 'Toegevoegd' : 'Lukt niet';
          setTimeout(() => { knop.querySelector('span').textContent = 'In winkelwagen'; knop.disabled = false; }, 1600);
        });
      }
      return li;
    }));
  }

  alles?.addEventListener('click', async () => {
    const producten = (await Promise.all(favorieten.producten().map(haalProduct))).filter(Boolean);
    const regels = producten.map((p) => p.variants.find((v) => v.available)).filter(Boolean)
      .map((v) => ({ id: v.id, quantity: 1 }));
    if (!regels.length) return;
    alles.disabled = true;
    const gelukt = await inMandje(regels);
    alles.querySelector('span').textContent = gelukt ? 'Alles toegevoegd' : 'Lukt niet';
    setTimeout(() => { alles.querySelector('span').textContent = 'Alles in winkelwagen'; alles.disabled = false; }, 1800);
  });

  document.addEventListener('fts:favorieten', teken);
  teken();
}

/* --- Start ---------------------------------------------------------------- */

document.addEventListener('fts:favorieten', () => {
  document.querySelectorAll('.fts-hart').forEach(zetHart);
  tellers();
});

account.start();
bindHartjes();
hartjesOpKaarten();
hartOpProductpagina();
tellers();
document.querySelectorAll('[data-fts-favorieten-pagina]').forEach(favorietenPagina);

// Kaarten die later verschijnen (filters, meer laden, aanbevelingen)
let gepland = false;
new MutationObserver(() => {
  if (gepland) return;
  gepland = true;
  requestAnimationFrame(() => { gepland = false; bindHartjes(); hartjesOpKaarten(); hartOpProductpagina(); });
}).observe(document.body, { childList: true, subtree: true });
